import { useId } from "react";
import {
  Button,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Stack,
  Switch,
  Typography,
} from "@altimateai/lego";
import { IconLoader2, IconSettings } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import type { ToggleAutoTunePreflight } from "@/pages/jobs/types";

interface Props {
  enabled: boolean;
  onToggle: (next: boolean) => void;
  isToggling?: boolean;
  preflight?: ToggleAutoTunePreflight | null;
  className?: string;
  /** Number of tasks where Auto Tune is currently enabled. */
  tasksEnabled?: number;
  /** Total Auto Tune tasks for this job (denominator). */
  tasksTotal?: number;
  /** Opens the Auto Tune Settings drawer. */
  onOpenSettings?: () => void;
  /** True while the parent's status query is still resolving. Renders
   *  the pill as inert without the saving spinner — we don't know
   *  yet whether the toggle is actionable, so neither "Saving…" (a
   *  write-side signal) nor the "no recs" copy is appropriate. */
  isLoading?: boolean;
}

// Header-level master Auto Tune switch. The toggle cascades the
// enable/disable to every task on the job. When the partial state
// applies (some tasks on, some off), the hover card surfaces the
// fraction and points users to the gear to manage per-task selection.
const AutoTuneStatusControl = ({
  enabled,
  onToggle,
  isToggling = false,
  preflight,
  className,
  tasksEnabled = 0,
  tasksTotal = 0,
  onOpenSettings,
  isLoading = false,
}: Props) => {
  const blocked = Boolean(preflight?.blocked);
  const blockedMessage = preflight?.actionable_message ?? null;
  // Stable id for the visually-hidden description so the Switch's
  // ``aria-describedby`` can point at it. Keyboard + screen-reader
  // users can't reach the HoverCard (it's pointer-only), so the
  // disabled reason has to live in an element that the AT can read
  // when the Switch receives focus.
  const describedById = useId();
  // No-recs state — distinct from ``blocked`` (which is a workspace /
  // permission failure). When the job has no Auto Tune-eligible tasks
  // there's nothing for the worker to apply, so the Switch is inert
  // and the hover card explains why.
  const noRecs = tasksTotal === 0;
  // Right-edge of the pill: the gear slot supplies its own padding
  // via the inner button's ``mx-2``. When there's no gear (no recs
  // OR no settings handler), we have to add ``al-pe-2`` ourselves
  // or the "Auto Tune" label sits flush against the border.
  const hasGearSlot = tasksTotal > 0 && Boolean(onOpenSettings);

  // Three states for hover-card copy + visual.
  const isPartial = tasksEnabled > 0 && tasksEnabled < tasksTotal;
  const isAllOn = tasksTotal > 0 && tasksEnabled === tasksTotal;

  // Plain-text description for keyboard / screen-reader users. The
  // HoverCard below only opens on pointer hover so it's invisible to
  // assistive tech; we mirror its "why is this disabled" copy into an
  // ``al-sr-only`` element and point ``aria-describedby`` at it. Only
  // populated for the unavailable states — the default copy ("Auto
  // Tune is disabled. Toggling on...") doesn't need a description.
  const describedByText = (() => {
    if (isLoading) return "Checking Auto Tune status.";
    if (blocked) {
      return (
        blockedMessage ??
        "Auto Tune is unavailable for this job. Workspace permissions or configuration prevent enabling it."
      );
    }
    if (noRecs) {
      return "Auto Tune is unavailable. No Auto Tune recommendations are available for this job yet.";
    }
    return null;
  })();

  const switchControl = (
    <Switch
      checked={enabled}
      disabled={isLoading || isToggling || blocked || noRecs}
      onCheckedChange={onToggle}
      aria-label={`Toggle Auto Tune ${enabled ? "off" : "on"}`}
      aria-describedby={describedByText ? describedById : undefined}
      // Deep brand blue (#004FBF) when checked — matches the existing
      // warehouse Auto Tune pill so both surfaces feel like one control.
      className="data-[state=checked]:!al-bg-[#004FBF]"
    />
  );

  // Hover-card content — overrides the default copy when the toggle
  // is actually clickable (i.e. preflight isn't blocked).
  const hoverContent = (() => {
    if (isLoading) {
      // Status query still in flight — surface a neutral signal
      // instead of the not-toggleable copy. Both "blocked" and
      // "no recs" require resolved data; until then we genuinely
      // don't know which (if any) applies.
      return <Typography size="sm">Checking Auto Tune status…</Typography>;
    }
    if (blocked) {
      return (
        <>
          <Typography size="sm" weight="medium" className="al-text-action-red al-mb-1">
            Auto Tune unavailable
          </Typography>
          <Typography size="sm">
            {blockedMessage ??
              "Workspace permissions or configuration prevent enabling Auto Tune on this job."}
          </Typography>
        </>
      );
    }
    if (noRecs) {
      // Symmetric with the blocked-by-preflight branch above so the
      // user gets a clear "not toggleable + reason" signal in either
      // failure mode.
      return (
        <>
          <Typography size="sm" weight="medium" className="al-text-action-red al-mb-1">
            Auto Tune unavailable
          </Typography>
          <Typography size="sm">
            No Auto Tune recommendations are available for this job yet.
          </Typography>
        </>
      );
    }
    if (isAllOn) {
      return (
        <Typography size="sm">
          Auto Tune is enabled for all {tasksTotal} tasks. Toggling off rolls back the cluster spec
          for every previously-applied task.
        </Typography>
      );
    }
    if (isPartial) {
      return (
        <Typography size="sm">
          Auto Tune is enabled for {tasksEnabled} of {tasksTotal} tasks. Open Auto Tune Settings to
          add or remove tasks.
        </Typography>
      );
    }
    return (
      <Typography size="sm">
        Auto Tune is disabled. Toggling on enables Auto Tune for all {tasksTotal} tasks.
      </Typography>
    );
  })();

  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <Stack
          direction="row"
          align="center"
          gap="md"
          data-autotune-enabled={enabled}
          // Mirrors the warehouse Auto Tune pill exactly — single
          // bordered container with the gear sharing the same surface
          // (separated by an inline ``al-border-l`` divider). Switching
          // off drops the brand chrome to a neutral gray pill so the
          // disabled state reads as inert. ``al-flex-shrink-0`` keeps
          // the pill at its natural size even when the page-header
          // title row gets wide enough to compete for space.
          className={cn(
            "al-ps-2 al-rounded-md al-border al-min-h-9 al-flex-shrink-0",
            !hasGearSlot && "al-pe-2",
            "data-[autotune-enabled=true]:al-border-stroke-brand-light data-[autotune-enabled=true]:al-bg-bg-brand data-[autotune-enabled=true]:al-text-brand",
            "data-[autotune-enabled=false]:al-bg-bg-gray data-[autotune-enabled=false]:al-text-gray",
            // Pre-flight ``blocked`` disables the Switch (above) but must NOT
            // grey out the whole pill — the gear stays an active affordance
            // for opening recommendations. Only fully-inert states (no recs,
            // still loading) dim the pill; the disabled Switch + red hover
            // card already signal that enablement is blocked.
            (noRecs || isLoading) && "al-cursor-not-allowed al-opacity-60",
            className
          )}
        >
          {switchControl}
          {describedByText && (
            // Visually hidden description bound to the Switch via
            // ``aria-describedby``. ``aria-live="polite"`` announces
            // state transitions (e.g. loading → blocked) without
            // interrupting the user.
            <span
              id={describedById}
              className="al-sr-only"
              aria-live="polite"
              data-testid="auto-tune-master-status-description"
            >
              {describedByText}
            </span>
          )}
          <Typography variant="body" size="sm" weight="medium">
            Auto Tune
          </Typography>
          {isToggling && (
            <Stack
              direction="row"
              align="center"
              gap="xs"
              role="status"
              aria-live="polite"
              data-testid="auto-tune-master-saving"
            >
              <IconLoader2 size={16} className="al-animate-spin" aria-hidden />
              <Typography size="xs" weight="medium">
                Saving…
              </Typography>
            </Stack>
          )}
          {hasGearSlot ? (
            // ``al-self-stretch`` makes the divider span the full pill
            // height; ``al-flex al-items-center`` then centers the gear
            // glyph inside that band so it sits on the same vertical
            // axis as the "Auto Tune" text.
            <div
              className={cn(
                "al-self-stretch al-border-l al-flex al-items-center",
                enabled ? "al-border-stroke-brand-light" : "al-border-stroke-light"
              )}
            >
              <Button
                size="icon"
                variant="ghost"
                title="Auto Tune Settings"
                aria-label="Auto Tune Settings"
                onClick={onOpenSettings}
                disabled={isToggling}
                className="hover:!al-bg-transparent !al-h-auto !al-w-auto !al-p-0 !al-mx-2 !al-rounded-none"
              >
                <IconSettings className="!al-size-5" />
              </Button>
            </div>
          ) : null}
        </Stack>
      </HoverCardTrigger>
      <HoverCardContent className="al-max-w-sm al-text-sm">{hoverContent}</HoverCardContent>
    </HoverCard>
  );
};

export default AutoTuneStatusControl;
